import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { BLOG_TITLE, BREADCRUMBS_BASE } from 'utils/shared';
import PostList from 'components/PostList';
import usePostsSearch from 'hooks/usePostsSearch';
import { getPaginatedPosts } from 'lib/wordpress';
import { BreadcrumbsItemType } from 'ui/src/components/Breadcrumbs';
import withLayoutHOF from 'components/withLayoutHOF';
// import { BLOG_INDEX_REVLIDATION_TIME } from 'pages/page/[page]';
import { InferGetStaticPropsType } from 'next';
import getPaginationHrefConstructor from 'utils/getPaginationHrefConstructor';

export default function Search({
  popularArticles,
  totalPopularArticles,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { query, isReady } = useRouter();
  const subtitle = typeof query.value === 'string' ? query.value : '';
  const page = typeof query.page === 'string' && !Number.isNaN(Number(query.page)) ? Number(query.page) : 1;
  const { posts, isLoading, total, isLoadingTotal } = usePostsSearch(subtitle, page);
  const paginationHrefConstructor = getPaginationHrefConstructor(
    posts.length === 0
      ? { queryPageKey: 'page', href: { query, pathname: '/search/not-found/page/[page]' } }
      : { queryPageKey: 'page', href: { query: { ...query, page } } },
  );

  const shownPosts = posts.length === 0 ? popularArticles : posts;

  const breadcrumbsItems: BreadcrumbsItemType[] = [
    ...BREADCRUMBS_BASE,
    {
      label: 'Search results',
    },
  ];

  const title =
    total !== 0 ? (
      'Results for:'
    ) : (
      <>
        Sorry, no results for <span className="text-site-neutrals-500 break-words">{subtitle}</span>, but you might
        be interested on these popular articles:
      </>
    );

  return (
    <>
      <Head>
        <title>
          {BLOG_TITLE} | {subtitle}
        </title>
      </Head>
      <PostList
        isLoading={isLoading || !isReady}
        title={title}
        subtitle={total !== 0 ? subtitle : undefined}
        posts={shownPosts}
        total={total || totalPopularArticles}
        breadcrumbs={breadcrumbsItems}
        withCategories
        currentPage={page}
        isLoadingTitle={isLoadingTotal || !isReady}
        paginationHrefConstructor={paginationHrefConstructor}
      />
    </>
  );
}

// const SEARCH_REVALIDATION_TIME = BLOG_INDEX_REVLIDATION_TIME;

export const getStaticProps = withLayoutHOF(async () => {
  const { data, errors } = await getPaginatedPosts();

  if (errors) {
    throw new Error(`PAGINATED_POST_QUERY_OBJECT: ${JSON.stringify(errors)}`);
  }

  const popularArticles =
    data?.posts?.edges?.map((post) => {
      if (!post?.node) return null;
      return { ...post.node, date: post.node?.date ? new Date(post.node.date).toUTCString() : '' };
    }) || [];

  const totalPopularArticles = data?.posts?.pageInfo?.offsetPagination?.total ?? 0;
  const seo = {
    robots: ['follow', 'noindex', 'noarchive', 'noimageindex', 'nosnippet'],
  };
  return {
    props: { popularArticles, totalPopularArticles, seo },
    // revalidate: SEARCH_REVALIDATION_TIME, // ISR
  };
}, 'Search result');
